import {apiFetch} from '../../../security/apiFetch';
import {useCallback,useEffect,useMemo,useRef,useState} from 'react';
interface AllahName {
  id: string;
  number?: number;
  name_arabic: string;
  name_latin?: string;
  name_ascii?: string;
  name_cyr?: string;
  translations: string[];
  quran_references: Array<{
    surah: number;
    ayah_start: number;
    ayah_end?: number;
    reference_text?: string;
  }>;
  notes: string[];
  metadata: Record<string, any>;
  properties?: {
    meaning_detailed?: string;
    linguistic_root?: string;
    benefits?: string;
    when_to_use?: string;
    related_attributes: string[];
    dua_examples: string[];
  };
  audio?: {
    storage_path: string;
    filename: string;
    reciter_name?: string;
    mime_type?: string;
    duration_seconds?: number;
    size_bytes?: number;
  };
  times_viewed: number;
  is_learned: boolean;
  learned_at?: string;
}

interface AllahNamesProgress {
  total_names: number;
  learned_names: number;
  viewed_names: number;
  progress_percent: number;
  last_learned?: string;
  streak_days: number | null;
  progress: Record<string, {
    times_viewed: number;
    is_learned: boolean;
    learned_at?: string;
    last_viewed_at?: string;
  }>;
}


const emptyProgress:AllahNamesProgress={total_names:0,learned_names:0,viewed_names:0,progress_percent:0,streak_days:null,progress:{}};
export const useAllahNamesProgress=(token:string|null,_userId?:string)=>{
 const [allahNames,setNames]=useState<AllahName[]>([]),[progress,setProgress]=useState(emptyProgress),[loading,setLoading]=useState(true),[saving,setSaving]=useState(0),[error,setError]=useState<string|null>(null),[searchQuery,setSearch]=useState('');
 const epoch=useRef(0),sequence=useRef(0),queues=useRef(new Map<string,Promise<boolean>>()),pending=useRef(new Map<string,Promise<boolean>>());
 const reload=useCallback(async()=>{
  const current=epoch.current,read=++sequence.current;setLoading(true);setError(null);
  try{
   if(!token)throw new Error();const options={headers:{Authorization:`Bearer ${token}`}};
   const [catalog,stored]=await Promise.all([apiFetch('/api/v1/allah-names/?limit=99',options),apiFetch('/api/v1/allah-names/progress',options)]);
   if(!catalog.ok||!stored.ok)throw new Error();const [data,summary]=await Promise.all([catalog.json(),stored.json()]);
   if(!Array.isArray(data.items)||!summary.progress||!Number.isFinite(summary.total_names)||!Number.isFinite(summary.learned_names))throw new Error();
   if(current!==epoch.current||read!==sequence.current)return false;
   setProgress(summary);setNames(data.items.map((item:AllahName)=>({...item,...(summary.progress[item.id]||{times_viewed:0,is_learned:false,learned_at:undefined})})));
   return true;
  }catch{
   if(current===epoch.current&&read===sequence.current){setNames([]);setProgress(emptyProgress);setError('Имена и сохранённый прогресс недоступны. Повторите загрузку.');}
   return false;
  }finally{if(current===epoch.current&&read===sequence.current)setLoading(false);}
 },[token]);
 useEffect(()=>{const version=epoch;setSaving(0);void reload();return()=>{version.current++;};},[reload]);
 const write=useCallback((id:string,action:'mark-learned'|'unmark-learned'|'view')=>{
  const current=epoch.current,owner=current+':'+id,key=owner+':'+action;
  const existing=pending.current.get(key);if(existing&&queues.current.get(owner)===existing)return existing;
  if(!token||!allahNames.some(name=>name.id===id)){setError('Имя недоступно. Обновите список.');return Promise.resolve(false);}
  setSaving(value=>value+1);
  const previous=queues.current.get(owner);
  const operation=(async()=>{
   try{
    await previous;
    if(current!==epoch.current)return false;
    const options={method:'POST',headers:{Authorization:`Bearer ${token}`,'Idempotency-Key':crypto.randomUUID()}};
    const response=action==='mark-learned'?await apiFetch('/api/v1/allah-names/'+encodeURIComponent(id)+'/mark-learned',{...options,method:'POST'}):action==='unmark-learned'?await apiFetch('/api/v1/allah-names/'+encodeURIComponent(id)+'/unmark-learned',{...options,method:'POST'}):await apiFetch('/api/v1/allah-names/'+encodeURIComponent(id)+'/view',{...options,method:'POST'});
    if(!response.ok)throw new Error();const data=await response.json();
    if(data.success!==true||data.content_id!==id)throw new Error();
    if(current!==epoch.current)return false;
    return await reload();
   }catch{if(current===epoch.current)setError('Изменение прогресса не подтверждено сервером.');return false;}
   finally{if(current===epoch.current)setSaving(value=>Math.max(0,value-1));}
  })();pending.current.set(key,operation);queues.current.set(owner,operation);
  void operation.then(()=>{if(pending.current.get(key)===operation)pending.current.delete(key);if(queues.current.get(owner)===operation)queues.current.delete(owner);});return operation;
 },[token,allahNames,reload]);
 const filteredNames=useMemo(()=>{const query=searchQuery.trim().toLocaleLowerCase();return query?allahNames.filter(name=>[name.name_arabic,name.name_latin,name.name_ascii,name.name_cyr,...name.translations].some(value=>value?.toLocaleLowerCase().includes(query))):allahNames;},[allahNames,searchQuery]);
 const getNextName=useCallback((from?:AllahName|null)=>{if(!allahNames.length)return null;const index=from?allahNames.findIndex(name=>name.id===from.id):-1;const ordered=[...allahNames.slice(index+1),...allahNames.slice(0,index+1)];return ordered.find(name=>!name.is_learned)||ordered[0]||null;},[allahNames]);
 const getRandomName=useCallback(()=>allahNames.length?allahNames[Math.floor(Math.random()*allahNames.length)]:null,[allahNames]);
 return {allahNames,filteredNames,progress,currentName:allahNames.find(name=>!name.is_learned)||allahNames[0]||null,isLoading:loading||saving>0,isSearching:false,error,searchQuery,
  loadAllahNames:reload,loadProgress:reload,searchNames:async(query:string)=>{setSearch(query);},
  markAsLearned:(id:string)=>write(id,'mark-learned'),unmarkAsLearned:(id:string)=>write(id,'unmark-learned'),markAsViewed:(id:string)=>write(id,'view'),getNextName,getRandomName};
};
